import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiCircle, FiClock, FiAlertCircle } = FiIcons;

const ProjectTimeline = ({ milestones, startDate, deadline }) => {
  // Sort milestones by date
  const sortedMilestones = [...milestones].sort((a, b) => new Date(a.date) - new Date(b.date));

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'in_progress': return FiClock;
      case 'delayed': return FiAlertCircle;
      default: return FiCircle;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'delayed': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-400 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="py-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Project Timeline</h3>
      
      <div className="relative border-l-2 border-gray-200 ml-4 space-y-8">
        {/* Start Node */}
        <div className="relative pl-8">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow-sm"></div>
          <div className="mb-1">
            <span className="text-sm font-semibold text-gray-900">Project Start</span>
            <span className="text-xs text-gray-500 ml-2">
              {startDate ? format(new Date(startDate), 'MMM dd, yyyy') : 'Not set'}
            </span>
          </div>
        </div>

        {/* Milestones */}
        {sortedMilestones.map((milestone, index) => (
          <motion.div 
            key={milestone.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative pl-8"
          >
            <div className={`absolute -left-[13px] top-0 p-1 rounded-full bg-white border-2 shadow-sm ${
              milestone.status === 'completed' ? 'border-green-500' : 
              milestone.status === 'in_progress' ? 'border-blue-500' : 
              milestone.status === 'delayed' ? 'border-red-500' : 'border-gray-300'
            }`}>
              <SafeIcon 
                icon={getStatusIcon(milestone.status)} 
                className={`w-4 h-4 ${
                  milestone.status === 'completed' ? 'text-green-600' : 
                  milestone.status === 'in_progress' ? 'text-blue-600' : 
                  milestone.status === 'delayed' ? 'text-red-600' : 'text-gray-400'
                }`} 
              />
            </div>
            
            <div className={`p-4 rounded-lg border ${getStatusColor(milestone.status)}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-gray-900">{milestone.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{format(new Date(milestone.date), 'MMM dd, yyyy')}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full uppercase font-medium tracking-wide ${
                  milestone.status === 'completed' ? 'bg-green-100 text-green-700' : 
                  milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 
                  milestone.status === 'delayed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {milestone.status.replace('_', ' ')}
                </span>
              </div>
            </div>
          </motion.div>
        ))}

        {/* End Node */}
        <div className="relative pl-8">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-gray-900 border-2 border-white shadow-sm"></div>
          <div className="mb-1">
            <span className="text-sm font-semibold text-gray-900">Target Deadline</span>
            <span className="text-xs text-gray-500 ml-2">
              {deadline ? format(new Date(deadline), 'MMM dd, yyyy') : 'Not set'}
            </span>
          </div>
        </div>
      </div>

      {milestones.length === 0 && (
        <div className="text-center py-8 text-gray-500 italic">
          No milestones added yet.
        </div>
      )}
    </div>
  );
};

export default ProjectTimeline;